import { useLazyQuery, useMutation } from "@apollo/client";
import { GENERATE_REPORT, POLL_REPORT_STATUS } from "../graphql/report-gql";
import { IReportParamsInput, IReportResponse } from "../types/report";
import { useRef, useState } from "react";
import { notifyError } from "../services/notification";
import useAuthToken from "./useAuthToken";
import { saveAs } from 'file-saver';
import { ReportType } from "../types/constants";

export default function useReport() {

    const [initReport] = useMutation(GENERATE_REPORT)
    const [fetchStatus] = useLazyQuery(POLL_REPORT_STATUS)

    const [inProgress, setInProgress] = useState(false)

    // the user could download another report before the first one has finished generating, 
    // so this needs to hold each specific completion reference on its own
    const intervalRefs = useRef<Map<string, any>>(new Map())  

    const [getAccessToken] = useAuthToken()

    const generateReport = (input: IReportParamsInput) => {
        setInProgress(true)
        initReport({
            variables: {
                input: input
            },
            onError: (error) => {
                setInProgress(false);
            },
            onCompleted: (data) => {
                let resp = data?.geneateReport
                startPolling(input.reportType, resp.reportId, resp.id)
            }
        })
    }

    const startPolling = (reportType: ReportType, reportId: string, exportId: string) => {
        const interval = setInterval(() => {
            fetchReportStatus(reportType, reportId, exportId)
        }, 4000);
        intervalRefs.current.set(exportId, interval)
    }

    const stopPolling = (exportId: string) => {
        const interval = intervalRefs.current.get(exportId)
        if (interval) {
            clearInterval(interval);
            intervalRefs.current.delete(exportId)
        }
    }

    const fetchReportStatus = (reportType: ReportType, reportId: string, exportId: string) => {
        fetchStatus({
            variables: {
                reportId: reportId,
                exportId: exportId
            },
            fetchPolicy: 'network-only'
        })
            .then(async (response: any) => {
                if (response?.data?.pollReportStatus) {
                    let resp: IReportResponse = response.data.pollReportStatus
                    if (resp.status === 'Succeeded') {
                        stopPolling(exportId)
                        await downloadReport(reportType, resp.resourceLocation)
                    } else if (resp.status === 'Failed') {
                        stopPolling(exportId)
                        setInProgress(false)
                        notifyError("Failed to fetch report status")
                    }
                }
            })
            .catch((error) => {
                stopPolling(exportId)
                setInProgress(false)
                console.error('Error fetching report status:', error)
            });
    }

    const downloadReport = async (reportType: ReportType, downloadUrl: string) => {
        console.log("Download report from : " + downloadUrl)

        try {
            const token = await getAccessToken()

            const response = await fetch('/api/report/download', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    'resourceLocation': downloadUrl
                })
            });

            if (!response.ok) {
                notifyError('Failed to download report')
                return
            }

            const blob = await response.blob()

            console.log("Report downloaded at: " + new Date().toISOString())

            let fileName: string = reportType === ReportType.RPT_MINISTRY_DATA ? 'ministry_data.pdf'
                : reportType === ReportType.RPT_HIERARCHY ? 'oms_hierarchy.pdf'
                    : reportType === ReportType.RPT_PIT_DETAILS ? 'pit_details.pdf'
                        : 'pit_hierarchy.pdf'

            saveAs(blob, fileName)
            setInProgress(false)
        } catch (error) {
            console.log('Error downloading the file:', error)
            notifyError('Failed to download report')
            setInProgress(false)
        }
    }

    return { generateReport, inProgress }
}